/**
 * Copyright (C) Siemens AG 1996-2024 ALL RIGHTS RESERVED.
 */

import {Component, OnDestroy, OnInit, inject} from '@angular/core';
import {firstValueFrom, Subscription} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {PassportService} from '../../../../services/passport.service';
import {ModalService, ToastService} from '@siemens/ix-angular';
import {QrCodeModalComponent} from '../../../shared/modals/qr-code-modal/qr-code-modal.component';
import {RemovePassportModalComponent} from '../../../shared/modals/remove-passport-modal/remove-passport-modal.component';
import {TranslateService} from '@ngx-translate/core';
import {WindowSizeService} from '../../../../services/window-size.service';
import {SchemaDTO, UISchemaDTO} from '../../../../models/Schema';
import {PassportDto} from '../../../../models/Passport';
import {EXTERNAL_ROLES} from '../../../../models/access-request.dto';
import {NgxRolesService} from 'ngx-permissions';
import {UserService} from '../../../../services/user.service';

@Component({
  selector: 'app-private-battery-pass-viewer',
  templateUrl: './private-battery-pass-viewer.component.html',
  styleUrl: './private-battery-pass-viewer.component.scss',
  standalone: false,
})
export class PrivateBatteryPassViewerComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly passportService = inject(PassportService);
  private readonly toastService = inject(ToastService);
  private readonly translate = inject(TranslateService);
  private readonly roleService = inject(NgxRolesService);
  private readonly userService = inject(UserService);
  private readonly modalService = inject(ModalService);

  protected readonly WindowSizeService = WindowSizeService;

  private routeSubscription?: Subscription;
  passId = '';
  passDisplayName?: string;
  passport?: PassportDto;

  loading = true;
  showTabs = true;
  passFound = true;
  passPublic = false;

  schema: SchemaDTO['data'];
  uiSchema: UISchemaDTO['data'];
  schemaName = '';

  historyInteractionExpanded = false;

  async ngOnInit() {
    this.routeSubscription = this.route.params.subscribe(async (params) => {
      this.passId = params['id'];
      try {
        this.passport = await this.passportService.loadOne(this.passId, true);
        this.passDisplayName =
          this.passport?.metadata?.name ?? this.passport?.id;
        this.passFound = true;
      } catch {
        try {
          this.passport = await this.passportService.loadOnePublic(this.passId);
          this.passDisplayName = this.passport?.id;
          this.passPublic = true;
          this.passFound = true;
          this.translate
            .get('battery_pass.private.missing_permissions', {
              id: this.passDisplayName,
            })
            .subscribe((msg: string) => {
              this.toastService.show({message: msg, type: 'warning'});
            });
        } catch {
          this.passFound = false;
        }
      }
      const schemaDTO = await this.passportService.loadSchema(
        this.passport?.metadata?.schemaId ?? '',
      );
      this.schema = schemaDTO.data;
      this.schemaName = this.schema.name;
      try {
        const uiSchemaDTO = await firstValueFrom(
          this.passportService.loadUiSchema$(
            this.passport?.metadata?.schemaId ?? '',
          ),
        );
        this.uiSchema = uiSchemaDTO.data;
      } catch {
        // uiSchema is optional — a missing or failing uiSchema does not block passport load
      }
      this.loading = false;
    });
  }

  ngOnDestroy() {
    this.routeSubscription?.unsubscribe();
  }

  async onBackButtonClick() {
    // NgxRolesService.getRoles() returns an object: { ROLE_NAME: {} }
    const roles = Object.keys(this.roleService.getRoles());
    const hasListPassportsRole = roles.some((role) =>
      ['DATA_MANAGER', 'SYS_ADMIN'].includes(role),
    );
    const hasDashboardRole = roles.some((role) =>
      EXTERNAL_ROLES.includes(role),
    );
    if (hasDashboardRole && !hasListPassportsRole) {
      await this.router.navigate(['/app/dashboard']);
    } else {
      await this.router.navigate(['/app/list-passports']);
    }
  }

  async exportPass(type: string) {
    const passData = await this.passportService.exportPass(this.passId, type);

    if (passData) {
      const anchorElement = document.createElement('a');

      const file = new Blob(
        [type === 'json' ? JSON.stringify(passData) : passData],
        {type},
      );
      const dataURL = window.URL.createObjectURL(file);

      anchorElement.href = dataURL;
      anchorElement.download = `${this.passId}.${type}`;
      anchorElement.click();
      URL.revokeObjectURL(dataURL);
      anchorElement.remove();

      this.translate
        .get('battery_pass.private.export_success_message')
        .subscribe((msg: string) => {
          this.toastService.show({message: msg, type: 'success'});
        });
    } else {
      this.translate
        .get('battery_pass.private.export_error_message')
        .subscribe((msg: string) => {
          this.toastService.show({message: msg, type: 'error'});
        });
    }
  }

  onHistoryButtonClick() {
    this.historyInteractionExpanded = !this.historyInteractionExpanded;
  }

  historyPaneExpandedChanged(event: Event) {
    setTimeout(() => {
      this.historyInteractionExpanded = (event as CustomEvent).detail.expanded;
    }, 0);
  }

  async onShowQrClick() {
    await this.modalService.open({
      size: '360',
      content: QrCodeModalComponent,
      data: {
        width: 240,
        data: this.passport?.metadata?.qrCodeContent,
        margin: 3,
      },
      centered: true,
    });
  }

  async onRemoveClick() {
    const id = this.passId;
    if (!id) {
      return;
    }

    const name = this.passport?.metadata?.name;
    const instance = await this.modalService.open({
      content: RemovePassportModalComponent,
      centered: true,
      data: {
        passport: this.passport,
        name: name ?? id,
      },
    });

    instance.onClose.on(async (shouldRemove: boolean) => {
      if (shouldRemove) {
        await firstValueFrom(this.passportService.delete$(id));
        this.translate
          .get('battery_pass.list.removing_battery_pass_success')
          .subscribe((msg: string) => {
            this.toastService.show({message: msg, type: 'success'});
          });
        await this.onBackButtonClick();
      }
    });
  }

  /**
   * Checks if the current user has ownership of the passport.
   * Returns true if the user's organization ID matches the passport's organization ID.
   *
   * @returns {boolean} - True if user's organization owns the passport, false otherwise.
   */
  hasPassportOwnership(): boolean {
    const userOrgId = this.userService.userData?.organization?.id;
    const passportOrgId = this.passport?.metadata?.organizationId;
    return userOrgId === passportOrgId;
  }
}
